import { memo, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { batch, useDispatch, useSelector } from 'react-redux';

import { ElasticFormModal, InfoModal } from 'basic-components';
import { DataLakeQuery } from 'dashboard-services/api/dataLake';
import Query from 'dashboard-services/api/Query';
import { ReduxService } from 'dashboard-services/services';
import { Markdown, markdownEditorActions, MarkdownPreviewer, statusBoxActions } from 'primary-components';

import getApiConfig from 'api/ApiConfig';
import { filesDatalakeQueries } from 'queries/modules/datalake';
import { sendEvent } from 'services/GAService';

import FileDetails from 'basic/FileDetails';

import { useQueryClient } from '@tanstack/react-query';
import { Else, If, Then, When } from 'react-if';

import './MarkDownEditor.scss';

const MarkDownEditor = () => {
  const dispatch = useDispatch(),
        { editable = false, fileInfo, newFile, open, optionalText, provider, onSubmitCallback } = useSelector(state => state.primaryComponentsState.markdownEditorState),
        editorRef = useRef(),
        formRef = useRef(),
        [content, setContent] = useState(undefined),
        [fileInfoLoc, setFileInfoLoc] = useState(undefined),
        [errors, setErrors] = useState([]),
        [areFileDetailsEditable, setAreFileDetailsEditable] = useState(newFile),
        hide = useCallback(() => dispatch(markdownEditorActions.clean()), [dispatch]),
        getQueryBuilderFileContentUpload = useCallback(() => 
          new Query.Builder(dispatch(getApiConfig()))
        , [dispatch]),
        getQueryBuilderFileDetails = useCallback(() => 
          new Query.Builder(dispatch(getApiConfig()))
            .withHeader("Target", provider, true)
            .withOnError(({ response }) => setErrors(response?.validationErrors))
            .withOnParse(response => Promise.resolve(response))
        , [dispatch, provider]),
        getQueryBuilderFileContent = useCallback(() => 
          new Query.Builder(dispatch(getApiConfig()))
            .withOnError(({ response }) => setErrors(response?.validationErrors))
            .withOnParse(response => Promise.resolve(response))
        , [dispatch]),
        queryClient = useQueryClient(),
        { mutateAsync: uploadFileMetadataCall } = filesDatalakeQueries.useUploadFileMetadata({ getQueryBuilder: getQueryBuilderFileContent }),
        { mutateAsync: uploadFileContentCall } = filesDatalakeQueries.useUploadFileContent({ getQueryBuilder: getQueryBuilderFileContentUpload }),
        timeoutRef = useRef(),
        isPending = useRef(false),
        uploadContent = useCallback(() => {
          if(isPending.current) {
            return Promise.resolve()
          }
          if(areFileDetailsEditable) {
            formRef.current?.validateForForm()
            if(!formRef.current?.isValid()) {
              return Promise.resolve()
            }
          }
          batch(() => {
            fileInfoLoc?.fileName && batch(() => {
              dispatch(markdownEditorActions.changeValue(ReduxService.updateObject(fileInfo, {
                fileName: fileInfoLoc?.fileName,
                groupName: fileInfoLoc?.groupName
              }), "fileInfo"))
              dispatch(markdownEditorActions.changeValue(({ jobId }) => dispatch(statusBoxActions.addJob({ jobId, displayName: fileInfoLoc?.fileName, subtitle: fileInfoLoc?.groupName })), 'jobIdCallback'))
              dispatch(markdownEditorActions.changeValue(hide , 'saveCallback'))
              dispatch(markdownEditorActions.changeValue(setErrors, "onValidationError"))
            })
            dispatch(markdownEditorActions.saveFile({
              content: editorRef?.current?.getContent?.(),
              uploadFileMetadataCall,
              uploadFileContentCall
            }))
            .then(() => timeoutRef.current = setTimeout(() => queryClient.invalidateQueries({ queryKey: DataLakeQuery.KEYS.SEARCH_FILES() }), 3000))
            .then(() => onSubmitCallback?.({ name: fileInfoLoc?.fileName, groupName: fileInfoLoc?.groupName }))
          })
        }, [areFileDetailsEditable, dispatch, fileInfo, fileInfoLoc?.fileName, fileInfoLoc?.groupName, hide, onSubmitCallback, queryClient, uploadFileContentCall, uploadFileMetadataCall]),
        { mutateAsync: getFileDetailsCall } = filesDatalakeQueries.useGetFileDefinitionMutation({ getQueryBuilder: getQueryBuilderFileDetails, id: fileInfo?.fid }),
        { mutateAsync: getFileContentCall } = filesDatalakeQueries.useGetFileContentMutation({ getQueryBuilder: getQueryBuilderFileContent }),
        downloadMarkdown = useCallback(() => {
          if(!fileInfo?.fid){
            return Promise.resolve()
          }
          isPending.current = true;
          return dispatch(markdownEditorActions.getFile({ getFileDetailsCall, getFileContentCall }))
            .then(response => setContent(response))
            .finally(() => {
              isPending.current = false;
            })
        }, [dispatch, fileInfo?.fid, getFileContentCall, getFileDetailsCall]),
        fileHeader = useMemo(() => (
          <When condition={fileInfoLoc !== undefined}>
            <FileDetails
                areFileDetailsEditable={areFileDetailsEditable}
                className="ng-markdown__header"
                editable={editable}
                errors={errors}
                extension="md"
                fileInfo={fileInfoLoc}
                groupNameLoc={fileInfoLoc?.groupName}
                newFile={newFile}
                ref={formRef}
                setAreFileDetailsEditable={setAreFileDetailsEditable}
                setFileInfo={setFileInfoLoc}
            />
          </When>
        ), [areFileDetailsEditable, editable, errors, fileInfoLoc, newFile])

  useEffect(() => { // file from data lake
    !content && downloadMarkdown()
  }, [content, downloadMarkdown])

  useEffect(() => {
    setFileInfoLoc(fileInfo)
  },[fileInfo])

  useEffect(() => () => {
    hide()
    clearTimeout(timeoutRef.current)
  }, [hide])

  useEffect(() => {
    const event = {
      category: "view_item",
      action: "markdown_editor",
      label: "tool",
    };
    dispatch(sendEvent(event))
  }, [dispatch])

  return (
    <When condition={open && (content || newFile)}>
      {() => (
        <If condition={!editable}>
          <Then>
            {() => (
              <InfoModal
                  className="ng-markdown-viewer"
                  onClose={hide}
                  open
                  optionalText={optionalText}
                  size="large"
                  sizeWidth="large"
                  title={fileInfo?.title}
              > 
                <MarkdownPreviewer 
                    defaultContent={content}
                    ref={editorRef}
                />
              </InfoModal>
            )}
          </Then>
          <Else>
            {() => (
              <ElasticFormModal
                  className="ng-markdown-editor"
                  closeOnBackground={false}
                  onClose={hide}
                  onSubmit={uploadContent}
                  title={fileHeader}
              >
                <Markdown
                    defaultContent={content || (newFile && "")}
                    ref={editorRef}
                />
              </ElasticFormModal>
            )}
          </Else>
        </If>
      )}
    </When>
  )
}

export default memo(MarkDownEditor)